/**
 * Canonical wine-color normalization, label, palette, and order helpers.
 *
 * Backend `WineName.color` enum: RED, WHITE, ROSE, ORANGE, SPARKLING, DESSERT, FORTIFIED.
 * Some legacy paths use `PINK_ROSE` (a grape-domain alias) — normalize to ROSE.
 * Some legacy paths use `SWEET` for what backend stores as `DESSERT` — collapse to SWEET.
 *
 * One source of truth — every surface that displays, filters, groups, or
 * sorts wine colors must read from this module rather than duplicating maps
 * inline. Grape color (RED | WHITE | PINK_ROSE) is a separate domain and is
 * intentionally NOT covered here.
 */

export type WineColorCanonical =
  | 'RED'
  | 'WHITE'
  | 'ROSE'
  | 'SPARKLING'
  | 'SWEET'
  | 'FORTIFIED'
  | 'ORANGE';

const ALIAS_MAP: Record<string, WineColorCanonical> = {
  RED: 'RED',
  WHITE: 'WHITE',
  ROSE: 'ROSE',
  ROSÉ: 'ROSE',
  PINK_ROSE: 'ROSE',
  PINK: 'ROSE',
  SPARKLING: 'SPARKLING',
  SWEET: 'SWEET',
  DESSERT: 'SWEET',
  FORTIFIED: 'FORTIFIED',
  ORANGE: 'ORANGE',
};

export const normalizeWineColor = (
  value: string | null | undefined,
): WineColorCanonical | null => {
  if (!value) return null;
  const key = String(value).trim().toUpperCase();
  return ALIAS_MAP[key] ?? null;
};

export const WINE_COLOR_LABEL: Record<WineColorCanonical, string> = {
  RED: 'Red',
  WHITE: 'White',
  ROSE: 'Rosé',
  SPARKLING: 'Sparkling',
  SWEET: 'Sweet',
  FORTIFIED: 'Fortified',
  ORANGE: 'Orange',
};

export const WINE_COLOR_DOT: Record<WineColorCanonical, string> = {
  RED:       '#d13447', // saturated red
  WHITE:     '#f0c43d', // bright yellow / gold
  ROSE:      '#e07ba3', // pink
  SPARKLING: '#4caf6d', // clean green
  SWEET:     '#e88533', // orange / amber
  FORTIFIED: '#9b6cc4', // purple
  ORANGE:    '#cf7836', // orange wine — adjacent to sweet but distinguishable
};

export const NEUTRAL_WINE_DOT = '#8a8a8a';

/**
 * Canonical wine-color presentation order — applied wherever colors appear in
 * filters, grouped filters, sort modes, or option lists. Sole source of truth;
 * do not duplicate this sequence inline. Orange is part of the canonical list
 * and only renders in surfaces if the underlying data carries it. Unknown /
 * non-canonical values rank *after* every canonical entry (see
 * `compareWineColors`).
 */
export const WINE_COLOR_ORDER: WineColorCanonical[] = [
  'SPARKLING',
  'WHITE',
  'ORANGE',
  'ROSE',
  'RED',
  'SWEET',
  'FORTIFIED',
];

export const WINE_COLOR_RANK: Record<WineColorCanonical, number> =
  WINE_COLOR_ORDER.reduce((acc, key, idx) => {
    acc[key] = idx;
    return acc;
  }, {} as Record<WineColorCanonical, number>);

/** Rank assigned to anything that doesn't normalize to a canonical value. */
const WINE_COLOR_RANK_OTHER = WINE_COLOR_ORDER.length;

/** Numeric rank for a single value, with non-canonical values pushed to the end. */
export const wineColorRank = (value: string | null | undefined): number => {
  const canonical = normalizeWineColor(value);
  return canonical ? WINE_COLOR_RANK[canonical] : WINE_COLOR_RANK_OTHER;
};

/**
 * Comparator for canonical-order sorting. Accepts any input the normalizer
 * accepts (RED/red, ROSE/PINK_ROSE, DESSERT/SWEET, etc.). Unknown / null
 * values sort to the end. Stable when a === b.
 */
export const compareWineColors = (
  a: string | null | undefined,
  b: string | null | undefined,
): number => wineColorRank(a) - wineColorRank(b);

export const wineColorDotFor = (value: string | null | undefined): string => {
  const canonical = normalizeWineColor(value);
  return canonical ? WINE_COLOR_DOT[canonical] : NEUTRAL_WINE_DOT;
};

export const wineColorLabelFor = (
  value: string | null | undefined,
): string | null => {
  const canonical = normalizeWineColor(value);
  return canonical ? WINE_COLOR_LABEL[canonical] : null;
};
